import type { ContentPort } from '../../application/ports';
import {
  GENERIC_VALIDITY_NOTICE,
  CountryCode,
  requiredValidationsArePresent,
  LocalDate,
  QuestionId,
  SectionId,
  type CountryPack,
  type CountryPackSource,
  type CountryPackValidation,
  type Disclaimer,
  type FieldSpec,
  type Gender,
  type IslamicConcept,
  type FiqhRule,
  type ValidationRole,
  type LedgerRole,
  type MatrimonialRegime,
  type OwnerProfile,
  type Question,
  type RepeatableGroup,
  type Section,
} from '../../domain';

const SUPPORTED_SCHEMA_VERSION = '1.0.0';
type JsonObject = Readonly<Record<string, unknown>>;
export type ContentPackEnvironment = 'development' | 'review' | 'production';
export type ContentPackSource = (country: CountryCode) => Promise<unknown>;

declare const process: { readonly env: Readonly<Record<string, string | undefined>> };

export const resolveContentPackEnvironment = (
  value: string | undefined = process.env['CONTENT_PACK_ENVIRONMENT'],
): ContentPackEnvironment =>
  value === 'development' || value === 'review' || value === 'production' ? value : 'production';

export interface ContentDigest {
  digest(algorithm: 'SHA-256', data: Uint8Array<ArrayBuffer>): Promise<ArrayBuffer>;
}

export class ContentPackLoadError extends Error {
  constructor() {
    super('Country pack could not be loaded');
    this.name = 'ContentPackLoadError';
  }
}

export class ContentPackChecksumError extends Error {
  constructor() {
    super('Country pack checksum is missing, malformed, or does not match');
    this.name = 'ContentPackChecksumError';
  }
}

export class ContentPackSchemaError extends Error {
  constructor() {
    super('Country pack does not match schema 1.0.0');
    this.name = 'ContentPackSchemaError';
  }
}

export class ContentPackValidationError extends Error {
  constructor() {
    super('Production country packs require every declared human validation role');
    this.name = 'ContentPackValidationError';
  }
}

const failSchema = (): never => {
  throw new ContentPackSchemaError();
};

const isObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const objectValue = (value: unknown): JsonObject => (isObject(value) ? value : failSchema());
const stringValue = (value: unknown): string => (typeof value === 'string' ? value : failSchema());
const booleanValue = (value: unknown): boolean =>
  typeof value === 'boolean' ? value : failSchema();
const numberValue = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : failSchema();
const arrayValue = (value: unknown): readonly unknown[] =>
  Array.isArray(value) ? value : failSchema();
const nonEmptyString = (value: unknown): string => {
  const parsed = stringValue(value);
  return parsed.trim().length > 0 ? parsed : failSchema();
};

const canonicalJson = (value: unknown): string => {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') {
    return String(JSON.stringify(value));
  }
  if (typeof value === 'number' && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (isObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${canonicalJson(key)}:${canonicalJson(value[key])}`)
      .join(',')}}`;
  }
  throw new ContentPackChecksumError();
};

const bodyWithoutChecksum = (value: unknown): JsonObject => {
  if (!isObject(value) || !isObject(value['meta'])) throw new ContentPackChecksumError();
  const meta: Record<string, unknown> = {};
  for (const key of Object.keys(value['meta'])) {
    if (key !== 'checksum') meta[key] = value['meta'][key];
  }
  return Object.freeze({ ...value, meta: Object.freeze(meta) });
};

const webCryptoDigest: ContentDigest = Object.freeze({
  digest: (algorithm: 'SHA-256', data: Uint8Array<ArrayBuffer>) =>
    globalThis.crypto.subtle.digest(algorithm, data),
});

export const computeContentPackChecksum = async (
  value: unknown,
  digest: ContentDigest = webCryptoDigest,
): Promise<string> => {
  try {
    const bytes = new TextEncoder().encode(canonicalJson(bodyWithoutChecksum(value)));
    const hash = new Uint8Array(await digest.digest('SHA-256', bytes));
    return `sha256-${Array.from(hash, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
  } catch (error) {
    if (error instanceof ContentPackChecksumError) throw error;
    throw new ContentPackChecksumError();
  }
};

const defaultSource: ContentPackSource = async (country) => {
  let response: Response;
  try {
    response = await fetch(`/content-packs/${country.value}.json`);
  } catch {
    throw new ContentPackLoadError();
  }
  if (!response.ok) throw new ContentPackLoadError();
  try {
    const body: unknown = await response.json();
    return body;
  } catch {
    throw new ContentPackLoadError();
  }
};

const parseField = (value: unknown): FieldSpec => {
  const raw = objectValue(value);
  const common = Object.freeze({
    key: nonEmptyString(raw['key']),
    label: nonEmptyString(raw['label']),
    required: booleanValue(raw['required']),
  });
  switch (raw['kind']) {
    case 'text':
      return Object.freeze({ ...common, kind: 'text', multiline: booleanValue(raw['multiline']) });
    case 'money':
      return Object.freeze({ ...common, kind: 'money' });
    case 'number':
      return Object.freeze({ ...common, kind: 'number' });
    case 'boolean':
      return Object.freeze({ ...common, kind: 'boolean' });
    case 'country':
      return Object.freeze({ ...common, kind: 'country' });
    default:
      return failSchema();
  }
};

const parseQuestion = (value: unknown): Question => {
  const raw = objectValue(value);
  const gloss = raw['gloss'] === undefined ? undefined : objectValue(raw['gloss']);
  return Object.freeze({
    id: QuestionId.create(nonEmptyString(raw['id'])),
    label: nonEmptyString(raw['label']),
    field: parseField(raw['field']),
    ...(gloss === undefined
      ? {}
      : {
          gloss: Object.freeze({
            term: nonEmptyString(gloss['term']),
            definition: nonEmptyString(gloss['definition']),
          }),
        }),
  });
};

const parseQuestions = (value: unknown): readonly Question[] =>
  Object.freeze(arrayValue(value).map(parseQuestion));

const parseGroup = (value: unknown): RepeatableGroup => {
  const raw = objectValue(value);
  const common = {
    itemLabelSingular: nonEmptyString(raw['itemLabelSingular']),
    fields: Object.freeze(arrayValue(raw['fields']).map(parseField)),
  };
  if (raw['kindField'] === undefined) return Object.freeze(common);
  if (raw['kindField'] !== 'ledger') return failSchema();
  return Object.freeze({ ...common, kindField: 'ledger' });
};

const parsePart = (value: unknown): 1 | 2 | 3 | 4 =>
  value === 1 || value === 2 || value === 3 || value === 4 ? value : failSchema();

const parseLedgerRole = (value: unknown): LedgerRole | undefined => {
  if (value === undefined) return undefined;
  return value === 'debt' || value === 'credit' || value === 'bequest' || value === 'amanat'
    ? value
    : failSchema();
};

const parseIslamicConcept = (value: unknown): IslamicConcept | undefined => {
  if (value === undefined) return undefined;
  return value === 'wasi' ||
    value === 'mahr' ||
    value === 'ghusl' ||
    value === 'amanat' ||
    value === 'faraid'
    ? value
    : failSchema();
};

const parseMadhhab = (value: unknown): FiqhRule['madhhab'] => {
  if (
    value === 'maliki' ||
    value === 'hanafi' ||
    value === 'shafii' ||
    value === 'hanbali' ||
    value === 'unspecified'
  )
    return value;
  return failSchema();
};

const parseValidationRole = (value: unknown): ValidationRole => {
  if (value === 'fiqh' || value === 'civil') return value;
  return failSchema();
};

const parseRequiredValidations = (value: unknown): readonly ValidationRole[] => {
  const parsed = Object.freeze(arrayValue(value).map(parseValidationRole));
  return parsed.length > 0 ? parsed : failSchema();
};

const parseValidatedBy = (value: unknown): readonly CountryPackValidation[] =>
  Object.freeze(
    arrayValue(value).map((candidate) => {
      const raw = objectValue(candidate);
      return Object.freeze({
        role: parseValidationRole(raw['role']),
        validator: nonEmptyString(raw['validator']),
      });
    }),
  );

const parseRule = (value: unknown): FiqhRule => {
  const raw = objectValue(value);
  const certitude = raw['certitude'];
  if (certitude !== 'elevee' && certitude !== 'moyenne' && certitude !== 'a_verifier')
    return failSchema();
  const rule = Object.freeze({
    id: nonEmptyString(raw['id']),
    section: nonEmptyString(raw['section']),
    regle: nonEmptyString(raw['regle']),
    qualification: nonEmptyString(raw['qualification']),
    sources: Object.freeze(arrayValue(raw['sources']).map(nonEmptyString)),
    certitude,
    aVerifier: booleanValue(raw['aVerifier']),
    madhhab: parseMadhhab(raw['madhhab']),
    type: parseValidationRole(raw['type']),
  });
  return Object.freeze({
    ...rule,
    ...(raw['usage'] === undefined ? {} : { usage: nonEmptyString(raw['usage']) }),
  });
};

const parseRules = (value: unknown): readonly FiqhRule[] =>
  Object.freeze(arrayValue(value).map(parseRule));

const parsePredicate = (value: unknown): ((profile: OwnerProfile) => boolean) => {
  const raw = objectValue(value);
  if (raw['kind'] === 'always') return () => true;
  if (raw['kind'] !== 'profileEquals') return failSchema();
  const expected = raw['equals'];
  switch (raw['field']) {
    case 'gender':
      if (expected !== 'male' && expected !== 'female') return failSchema();
      return (profile) => profile.gender === expected;
    case 'maritalStatus':
      if (
        expected !== 'single' &&
        expected !== 'married' &&
        expected !== 'widowed' &&
        expected !== 'divorced'
      )
        return failSchema();
      return (profile) => profile.maritalStatus === expected;
    case 'burialPreference':
      if (expected !== 'residence' && expected !== 'origin' && expected !== 'other')
        return failSchema();
      return (profile) => profile.burialPreference === expected;
    case 'hasMinorChildren':
      if (typeof expected !== 'boolean') return failSchema();
      return (profile) => profile.hasMinorChildren === expected;
    case 'isConvert':
      if (typeof expected !== 'boolean') return failSchema();
      return (profile) => profile.isConvert === expected;
    default:
      return failSchema();
  }
};

const parseQuestionVariants = (
  value: unknown,
): Readonly<
  Record<Gender, { readonly title: string; readonly questions: readonly Question[] }>
> => {
  const raw = objectValue(value);
  const variant = (candidate: unknown) => {
    const item = objectValue(candidate);
    return Object.freeze({
      title: nonEmptyString(item['title']),
      questions: parseQuestions(item['questions']),
    });
  };
  return Object.freeze({ male: variant(raw['male']), female: variant(raw['female']) });
};

const parseCollectionVariants = (
  value: unknown,
): Readonly<
  Record<Gender, { readonly title: string; readonly itemTemplate?: RepeatableGroup }>
> => {
  const raw = objectValue(value);
  const variant = (candidate: unknown) => {
    const item = objectValue(candidate);
    const title = nonEmptyString(item['title']);
    return item['itemTemplate'] === undefined
      ? Object.freeze({ title })
      : Object.freeze({ title, itemTemplate: parseGroup(item['itemTemplate']) });
  };
  return Object.freeze({ male: variant(raw['male']), female: variant(raw['female']) });
};

const parseSection = (value: unknown): Section => {
  const raw = objectValue(value);
  const compartment: 'public' | 'private' =
    raw['compartment'] === 'public' || raw['compartment'] === 'private'
      ? raw['compartment']
      : failSchema();
  const ledgerRole = parseLedgerRole(raw['ledgerRole']);
  const islamicConcept = parseIslamicConcept(raw['islamicConcept']);
  const rules = raw['rules'] === undefined ? undefined : parseRules(raw['rules']);
  const common = {
    id: SectionId.create(nonEmptyString(raw['id'])),
    title: nonEmptyString(raw['title']),
    part: parsePart(raw['part']),
    compartment,
    showIf: parsePredicate(raw['showIf']),
    parcoursOrder: numberValue(raw['parcoursOrder']),
    documentOrder: numberValue(raw['documentOrder']),
    ...(ledgerRole === undefined ? {} : { ledgerRole }),
    ...(islamicConcept === undefined ? {} : { islamicConcept }),
    ...(rules === undefined ? {} : { rules }),
  };
  if (raw['answerShape'] === 'collection') {
    const section = {
      ...common,
      answerShape: 'collection',
      itemTemplate: parseGroup(raw['itemTemplate']),
    } satisfies Section;
    return raw['variantByGender'] === undefined
      ? Object.freeze(section)
      : Object.freeze({
          ...section,
          variantByGender: parseCollectionVariants(raw['variantByGender']),
        });
  }
  if (raw['answerShape'] === 'single') {
    const section = {
      ...common,
      answerShape: 'single',
      questions: parseQuestions(raw['questions']),
    } satisfies Section;
    return raw['variantByGender'] === undefined
      ? Object.freeze(section)
      : Object.freeze({
          ...section,
          variantByGender: parseQuestionVariants(raw['variantByGender']),
        });
  }
  if (raw['answerShape'] === 'counter') {
    const section = {
      ...common,
      answerShape: 'counter',
      questions: parseQuestions(raw['questions']),
    } satisfies Section;
    return raw['variantByGender'] === undefined
      ? Object.freeze(section)
      : Object.freeze({
          ...section,
          variantByGender: parseQuestionVariants(raw['variantByGender']),
        });
  }
  return failSchema();
};

const parseDisclaimer = (value: unknown): Disclaimer => {
  const raw = objectValue(value);
  return Object.freeze({
    code: nonEmptyString(raw['code']),
    text: nonEmptyString(raw['text']),
  });
};

const parsePack = (value: unknown, requestedCountry: CountryCode): CountryPack => {
  try {
    const raw = objectValue(value);
    const meta = objectValue(raw['meta']);
    if (meta['schemaVersion'] !== SUPPORTED_SCHEMA_VERSION) return failSchema();
    const validatedBy = parseValidatedBy(meta['validatedBy']);
    const requiredValidations = parseRequiredValidations(raw['requiredValidations']);
    const source =
      meta['source'] === undefined
        ? undefined
        : (() => {
            const rawSource = objectValue(meta['source']);
            if (rawSource['status'] !== 'à valider') return failSchema();
            return Object.freeze({
              name: nonEmptyString(rawSource['name']),
              version: nonEmptyString(rawSource['version']),
              status: 'à valider' as const,
            } satisfies CountryPackSource);
          })();
    const adapter = objectValue(raw['adapter']);
    const country = CountryCode.create(stringValue(adapter['country']));
    if (country.value !== requestedCountry.value) return failSchema();
    const posture =
      adapter['posture'] === 'orientation' ||
      adapter['posture'] === 'referral' ||
      adapter['posture'] === 'integration'
        ? adapter['posture']
        : failSchema();
    const disclaimers = objectValue(adapter['regimeDisclaimers']);
    const byRegime: Readonly<Record<MatrimonialRegime, Disclaimer>> = Object.freeze({
      community: parseDisclaimer(disclaimers['community']),
      separation: parseDisclaimer(disclaimers['separation']),
      participation: parseDisclaimer(disclaimers['participation']),
      unknown: parseDisclaimer(disclaimers['unknown']),
    });
    const registrationHint =
      adapter['registrationHint'] === undefined
        ? {}
        : { registrationHint: nonEmptyString(adapter['registrationHint']) };
    const partner =
      adapter['partnerHandoff'] === undefined
        ? {}
        : (() => {
            const handoff = objectValue(adapter['partnerHandoff']);
            return {
              partnerHandoff: Object.freeze({
                partnerId: nonEmptyString(handoff['partnerId']),
                label: nonEmptyString(handoff['label']),
              }),
            };
          })();
    return Object.freeze({
      meta: Object.freeze({
        schemaVersion: SUPPORTED_SCHEMA_VERSION,
        fiqhVersion: nonEmptyString(meta['fiqhVersion']),
        countryVersion: nonEmptyString(meta['countryVersion']),
        effectiveDate: LocalDate.create(stringValue(meta['effectiveDate'])),
        author: nonEmptyString(meta['author']),
        validatedBy,
        checksum: nonEmptyString(meta['checksum']),
        ...(source === undefined ? {} : { source }),
      }),
      adapter: Object.freeze({
        country,
        posture,
        holographicRequiresHandwriting: booleanValue(adapter['holographicRequiresHandwriting']),
        hasForcedHeirship: booleanValue(adapter['hasForcedHeirship']),
        // A pack that says nothing of validity gets the notice that claims nothing of its law.
        validityNotice:
          adapter['validityNotice'] === undefined
            ? GENERIC_VALIDITY_NOTICE
            : nonEmptyString(adapter['validityNotice']),
        regimeDisclaimer: (regime: MatrimonialRegime) => byRegime[regime],
        ...registrationHint,
        ...partner,
      }),
      requiredValidations,
      sections: Object.freeze(arrayValue(raw['sections']).map(parseSection)),
      // A fetched pack carries no civil notes yet: they come with the embedded modules.
      civilNotes: Object.freeze([]),
    });
  } catch (error) {
    if (error instanceof ContentPackSchemaError) throw error;
    throw new ContentPackSchemaError();
  }
};

export class ContentPackLoader implements ContentPort {
  constructor(
    private readonly source: ContentPackSource = defaultSource,
    private readonly environment: ContentPackEnvironment = 'production',
    private readonly digest: ContentDigest = webCryptoDigest,
  ) {}

  async load(country: CountryCode): Promise<CountryPack> {
    let raw: unknown;
    try {
      raw = await this.source(country);
    } catch (error) {
      if (error instanceof ContentPackLoadError) throw error;
      throw new ContentPackLoadError();
    }
    const expectedChecksum =
      isObject(raw) && isObject(raw['meta']) && typeof raw['meta']['checksum'] === 'string'
        ? raw['meta']['checksum']
        : undefined;
    if (expectedChecksum === undefined || !/^sha256-[0-9a-f]{64}$/.test(expectedChecksum))
      throw new ContentPackChecksumError();
    const checksum = await computeContentPackChecksum(raw, this.digest);
    if (checksum !== expectedChecksum) throw new ContentPackChecksumError();
    const pack = parsePack(raw, country);
    // The fiqh may wait for the scholar — the app says so in its validation banner — but the
    // law may not: production serves no civil rule I-DINA has not validated.
    const civil = pack.requiredValidations.filter((role) => role !== 'fiqh');
    if (
      this.environment === 'production' &&
      !requiredValidationsArePresent(civil, pack.meta.validatedBy)
    )
      throw new ContentPackValidationError();
    return pack;
  }
}
